import { useMutation, useQueryClient } from '@tanstack/react-query';
import { normalizeId } from '../../utils/idNormalization';
import { createCodeCacheUtils } from '../../utils/codeCacheUtils';


/**
 * Generic hook for updating a single field on a code with optimistic updates
 * @param {Function} apiFn - The API function to call
 * @param {string} errorMessage - Custom error message for logging
 * @returns {Object} The mutation object with mutate function and state
 */
function useUpdateCodeField(apiFn, errorMessage = 'Error updating code') {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  
  return useMutation({
    mutationFn: apiFn,
    
    onMutate: async (variables) => {
      const { codeId } = variables;
      const normalizedCodeId = normalizeId(codeId);
      
      await cacheUtils.cancelQueries(normalizedCodeId);
      const snapshot = await cacheUtils.getSnapshot(normalizedCodeId);
      
      // Extract the field updates (everything except codeId)
      const updates = Object.fromEntries(
        Object.entries(variables).filter(([key]) => key !== 'codeId')
      );
      
      cacheUtils.updateCode(normalizedCodeId, updates);
      
      return { ...snapshot, normalizedCodeId };
    },
    
    onError: (err, { codeId }, context) => {
      if (context) {
        const normalizedCodeId = context.normalizedCodeId || normalizeId(codeId);
        cacheUtils.restoreFromSnapshot(normalizedCodeId, context);
      }
      console.error(errorMessage, err);
    },    
  });
}

export default useUpdateCodeField;
